<template>
  <div
    v-if="isLoading"
    class="d-flex justify-center align-center"
    style="height: 100vh;"
  >
    <v-row no-gutters class="px-12 py-6">
      <v-col cols="12" class="d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="#2661FF"
          size="80"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </div>
  <div v-else>
    <v-row>
      <v-col cols="12">
        <div class="headline font-weight-bold mb-4">
          {{ $t('organization_billing.title') }}
        </div>
        <v-breadcrumbs
          divider=">"
          :items="breadcrumbItems"
          class="pa-0"
        ></v-breadcrumbs>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg" flat>
          <v-card-text>
            <v-row class="mt-4 mx-2">
              <v-col cols="3">
                <v-text-field
                  v-model="dataTable.search"
                  label="Buscar"
                  outlined
                  prepend-inner-icon="search"
                  clearable
                  class="rounded-lg"
                  @keyup.enter="getData"
                  @click:clear="clear('search')"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  v-if="plans.length"
                  v-model="dataTable.plan"
                  :items="plans"
                  item-text="name"
                  item-value="id"
                  label="Plan"
                  outlined
                  clearable
                  class="rounded-lg"
                  @click:clear="clear('plan')"
                  @change="getData"
                />
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  v-if="currencies.length"
                  v-model="dataTable.currency"
                  :items="currencies"
                  item-text="name"
                  item-value="id"
                  label="Moneda"
                  outlined
                  clearable
                  class="rounded-lg"
                  @click:clear="clear('currency')"
                  @change="getData"
                />
              </v-col>
              <v-col cols="3" class="d-flex justify-end">
                <v-btn
                  ref="button-open-export-dialog"
                  @click="toggleDialogExport(true)"
                  color="#2661ff"
                  class="mr-2 mt-3"
                >
                  <v-icon left style="color: #FFF;">cloud_download</v-icon>
                  <span style="color: #FFF;">{{ $t('general.export') }}</span>
                </v-btn>
              </v-col>
              <v-col cols="12" class="d-flex justify-end">
                <div class="d-flex">
                  <v-card outlined flat>
                    <v-row>
                      <v-menu
                        :close-on-content-click="false"
                        offset-y
                        nudge-bottom="10"
                      >
                        <template v-slot:activator="{ on }">
                          <v-col cols="12" sm="12">
                            <v-btn
                              v-on="on"
                              text
                              block
                              class="caption font-weight-medium grey--text text--darken-3 text-truncate"
                              max-width="100%"
                            >
                              {{ `${month}` }}
                            </v-btn>
                          </v-col>
                        </template>
                        <v-date-picker
                          v-model="month"
                          type="month"
                          @change="getData()"
                          color="#2661FF"
                          :max="monthLimit"
                        ></v-date-picker>
                      </v-menu>
                    </v-row>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-data-table
            :headers="dataTable.headers"
            :items="dataTable.items"
            :options="dataTable.pagination"
            :footer-props="{
              'items-per-page-options': [25, 50, 75, 100]
            }"
            :items-per-page="dataTable.pagination.itemsPerPage"
            :server-items-length="dataTable.total"
            class="elevation-1"
            pagination.sync="pagination"
            item-key="organization_id"
            :loading="dataTable.loading"
            no-data-text="Empty"
            @update:options="dataTable.pagination = $event"
          >
            <!-- SHOWS NO DATA -->
            <template v-slot:no-data>
              <div class="text-center pa-5 my-5">
                <v-img
                  :src="require('@/assets/svg/box_empty.svg')"
                  height="150"
                  max-height="150px"
                  aspect-ratio="1"
                  contain
                />
                <div class="grey--text">
                  <!-- {{ $t("transactions.dashboard.empty") }} -->
                  Sin datos
                </div>
              </div>
            </template>

            <template v-if="dataTable.items.length" v-slot:body="{ items }">
              <tbody>
                <tr
                  v-for="item in items"
                  :key="item.id"
                  @click="openBillingDetails(item)"
                >
                  <td>
                    {{ item.id }}
                  </td>
                  <td>
                    {{ item.organization.status }}
                  </td>
                  <td>
                    {{ item.organization.name }}
                  </td>
                  <td>
                    {{ item.plan.currency.code }}
                  </td>
                  <td>
                    {{ item.plan.display_name }}
                  </td>
                  <td>
                    {{
                      item.amount
                        | valueFormatCurrency(item.plan.currency.symbol)
                    }}
                  </td>
                  <td>
                    {{
                      item.collection_fee
                        | valueFormatCurrency(item.plan.currency.symbol)
                    }}
                  </td>
                  <td>
                    {{
                      item.services_amount
                        | valueFormatCurrency(item.plan.currency.symbol)
                    }}
                  </td>
                  <td>
                    {{
                      item.total
                        | valueFormatCurrency(item.plan.currency.symbol)
                    }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <BillingDetail v-if="isBillingDetailOpen" />

    <DialogExport
      :module-options="['OrganizationsBilling']"
      :filters="{ currency_code: dataTable.currency }"
      :start-time="startDate"
      :end-time="endDate"
    />
    <DialogMessage />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { organizationService } from '@/services/organization.service'
import DateFilters from '@/components/Common/DateFilters/Index'
import DialogExport from '@/components/Downloads/DialogExport'
import DialogMessage from '@/components/Downloads/DialogMessage'
import organizationMixin from '@/mixins/organization'
import donationMixin from '@/mixins/donations'
import BillingDetail from '../../components/Billing/BillingDetail.vue'

export default {
  name: 'BillingService',
  components: {
    DateFilters,
    DialogExport,
    DialogMessage,
    BillingDetail
  },
  mixins: [organizationMixin, donationMixin],
  data() {
    return {
      isLoading: false,
      dateRange: 3,
      month: this.$moment()
        .startOf('month')
        .format('YYYY-MM'),
      monthLimit: this.$moment()
        .endOf('month')
        .format('YYYY-MM'),
      startDate: '',
      endDate: '',
      breadcrumbItems: [
        {
          text: this.$t('organization.title'),
          disabled: true
        },
        {
          text: this.$t('organization_billing.billing_service.title'),
          disabled: false
        }
      ],
      dataTable: {
        loading: false,
        items: [],
        total: 0,
        search: null,
        currency: null,
        plan: null,
        pagination: {
          sortBy: ['id'],
          sortDesc: [false],
          itemsPerPage: 50
        },
        headers: [
          {
            text: 'ID',
            sortable: false,
            value: 'organization.id',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('common.status'),
            sortable: false,
            value: 'organization.status',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('general.organization'),
            sortable: false,
            value: 'organization.name',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization.org_currency'),
            sortable: false,
            value: 'plan.currency.name',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization_billing.plan'),
            sortable: false,
            value: 'plan.name',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization_billing.billing_service.fixed_amount'),
            sortable: false,
            value: 'amount',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t(
              'organization_billing.billing_service.collection_fee'
            ),
            sortable: false,
            value: 'collection_fee',
            class: 'header_color text-no-wrap'
          },
          {
            text: 'Services amount',
            sortable: false,
            value: 'services_amount',
            class: 'header_color text-no-wrap'
          },
          {
            text: this.$t('organization_billing.billing_service.total'),
            sortable: false,
            value: 'total',
            class: 'header_color text-no-wrap'
          }
        ]
      }
    }
  },
  async created() {
    try {
      this.isLoading = true
      await this.getCurrencies()
      await this.getData()
      await this.getPlans()
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },
  watch: {
    'dataTable.pagination': {
      async handler() {
        await this.getData()
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      currencies: ({ countries: { currencies } }) => currencies,
      plans: ({ plans: { plans } }) => plans,
      isBillingDetailOpen: ({ billings: { isBillingDetailOpen } }) =>
        isBillingDetailOpen
    })
  },
  methods: {
    ...mapActions({
      getCurrencies: 'countries/getCurrencies',
      getPlans: 'plans/getPlans'
    }),
    ...mapMutations({
      setBillingDetail: 'billings/setBillingDetail',
      toggleDialogExport: 'downloads/toggleDialogExport',
      toggleBillingDetail: 'billings/toggleBillingDetail'
    }),
    async getData() {
      try {
        this.dataTable.loading = true
        const [year, month] = this.month.split('-')
        const startDate = new Date(year, month - 1, 1)
        const endDate = new Date(year, month, 0)
        const startDateString = startDate.toISOString().slice(0, 10)
        const endDateString = endDate.toISOString().slice(0, 10)
        ;(this.startDate = startDateString), (this.endDate = endDateString)

        let { sortBy, sortDesc = false } = this.dataTable.pagination
        const { page, itemsPerPage: rowsPerPage } = this.dataTable.pagination
        const { search, currency, plan } = this.dataTable

        if (Array.isArray(sortBy)) {
          ;[sortBy] = sortBy
        }
        if (Array.isArray(sortDesc)) {
          ;[sortDesc] = sortDesc
        }

        const {
          data: { data, total }
        } = (
          await organizationService.getOrganizationsBilling({
            start_time: this.startDate,
            end_time: this.endDate,
            page,
            rowsPerPage,
            sortBy,
            descending: sortDesc,
            search,
            plan,
            currency
          })
        ).data

        this.dataTable.items = data
        this.dataTable.total = parseInt(total)
      } catch (error) {
        console.log(error)
      } finally {
        this.dataTable.loading = false
      }
    },
    async clear(type) {
      this.dataTable[type] = null
      await this.getData()
    },
    async openBillingDetails(billing) {
      this.toggleBillingDetail(true)
      this.setBillingDetail(billing)
    }
  }
}
</script>

<style>
.header_color {
  color: #2661ff !important;
}
</style>
